import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import CourseChrome from "../components/CourseChrome.js";
import {
  api,
  isStaffRole,
  termLabel,
  type AssignmentSummary,
  type Course,
  type ExamSummary,
} from "../api.js";
import { useAuth } from "../auth.js";

export default function Dashboard() {
  const { user } = useAuth();
  const isStaff = user ? isStaffRole(user.role) : false;
  const navigate = useNavigate();

  /**
   * Which course, and whether its settings are open, live in the URL. The tab
   * bar can then link to a section and highlight the one you are on, and going
   * to the roster and back keeps the course you were in.
   */
  const [params, setParams] = useSearchParams();
  const selected = params.get("course");
  const editingCourse = params.get("edit") === "1";

  const setSelected = (courseId: string | null, edit = false) => {
    const next = new URLSearchParams(params);
    // Whether the new-course panel is open is state from here on; leaving the
    // request in the URL would reopen it on every reload.
    next.delete("new");
    if (courseId) next.set("course", courseId);
    else next.delete("course");
    if (edit) next.set("edit", "1");
    else next.delete("edit");
    setParams(next, { replace: true });
  };

  const [courses, setCourses] = useState<Course[]>([]);
  const [assignments, setAssignments] = useState<AssignmentSummary[]>([]);
  const [exams, setExams] = useState<ExamSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  // The Courses tab has a New course button of its own, and creating one is a
  // dashboard form -- so it links here with the panel asked for in the URL.
  const [showNewCourse, setShowNewCourse] = useState(params.get("new") === "course");

  // Default to the current academic year; faculty create courses far more often
  // for the year they are in than any other.
  const [courseForm, setCourseForm] = useState({
    code: "",
    name: "",
    year: new Date().getFullYear(),
    semester: 1,
  });

  const [showArchived, setShowArchived] = useState(false);
  const [archivedCount, setArchivedCount] = useState(0);

  const loadCourses = (selectId?: string, includeArchived = showArchived) =>
    api
      .get<{ courses: Course[]; archivedCount: number }>(
        `/api/courses${includeArchived ? "?includeArchived=true" : ""}`
      )
      .then((r) => {
        setCourses(r.courses);
        setArchivedCount(r.archivedCount);
        // Keep the current selection if it is still in the list, otherwise fall
        // back -- archiving the selected course would leave a dead id in the URL.
        const stillThere = selected && r.courses.some((c) => c.id === selected);
        if (selectId) setSelected(selectId);
        else if (!stillThere) setSelected(r.courses[0]?.id ?? null);
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    void loadCourses(undefined, showArchived);
  }, [showArchived]);

  // The course lives in the URL, so anything that drops the query -- the brand
  // link home, a hand-typed "/" -- lands you on the dashboard with no course
  // while you plainly have one, and the sections that need a course id go dead.
  // Courses are only fetched when the archive filter changes, so put the
  // selection back here rather than refetching.
  useEffect(() => {
    if (!selected && courses.length) setSelected(courses[0].id);
  }, [selected, courses]);

  async function createCourse(e: React.FormEvent) {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setStatus(null);
    try {
      const r = await api.post<{ course: { id: string; code: string } }>("/api/courses", {
        code: courseForm.code.trim().toUpperCase(),
        name: courseForm.name.trim(),
        year: courseForm.year,
        semester: courseForm.semester,
      });
      setShowNewCourse(false);
      setCourseForm({ code: "", name: "", year: new Date().getFullYear(), semester: 1 });
      setStatus(`Created ${r.course.code}. You are enrolled as its teacher.`);
      await loadCourses(r.course.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the course.");
    } finally {
      setCreating(false);
    }
  }

  useEffect(reloadWork, [selected]);

  async function createExam() {
    if (!selected) return;
    setCreating(true);
    try {
      // Sensible defaults: opens now, closes in a week, 90-minute clock. Faculty
      // adjust in the builder rather than filling a form before seeing anything.
      const opensAt = new Date();
      const closesAt = new Date(Date.now() + 7 * 24 * 3600 * 1000);
      const r = await api.post<{ exam: { id: string } }>(`/api/courses/${selected}/exams`, {
        title: "Untitled exam",
        opensAt: opensAt.toISOString(),
        closesAt: closesAt.toISOString(),
        durationMinutes: 90,
      });
      window.location.href = `/exams/${r.exam.id}/edit`;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create the exam.");
      setCreating(false);
    }
  }

  async function createAssignment() {
    if (!selected) return;
    setCreating(true);
    try {
      const r = await api.post<{ assignment: { id: string } }>(`/api/courses/${selected}/assignments`, {
        title: "Untitled assignment",
        description: "",
      });
      // Start it as a sheet with one question. An assignment is a lab sheet in
      // everything but name, and beginning with an empty question list asks the
      // teacher to understand the distinction before they can type anything.
      await api.post(`/api/assignments/${r.assignment.id}/parts`, { kind: "PROGRAMMING" });
      window.location.href = `/assignments/${r.assignment.id}/edit`;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create the assignment.");
      setCreating(false);
    }
  }

  const course = courses.find((c) => c.id === selected);

  function reloadWork() {
    if (!selected) {
      // Nothing selected means nothing to show: leaving the last course's work
      // on screen reads as if it belonged to a course the header says you are
      // not in.
      setAssignments([]);
      setExams([]);
      return;
    }
    api
      .get<{ assignments: AssignmentSummary[] }>(`/api/courses/${selected}/assignments`)
      .then((r) => setAssignments(r.assignments))
      .catch((e) => setError(e.message));
    api
      .get<{ exams: ExamSummary[] }>(`/api/courses/${selected}/exams`)
      .then((r) => setExams(r.exams))
      .catch(() => {});
  }

  /**
   * Deleting is for things created by mistake. The server refuses once there is
   * student work or an exam depending on it, and says so -- the dialogue here
   * only has to stop the accidental click.
   */
  async function deleteAssignment(a: AssignmentSummary) {
    const parts = a.partCount ?? 0;
    if (
      !confirm(
        `Delete "${a.title}"?\n\n` +
          (parts > 0 ? `Its ${parts} question(s) go with it. ` : "") +
          `This cannot be undone. If students have submitted, unpublish it instead.`
      )
    )
      return;
    setError(null);
    setStatus(null);
    try {
      await api.del(`/api/assignments/${a.id}`);
      setStatus(`Deleted "${a.title}".`);
      reloadWork();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the assignment.");
    }
  }

  async function deleteExam(e: ExamSummary) {
    if (!confirm(`Delete "${e.title}"?\n\nThis cannot be undone. Its questions are not deleted.`))
      return;
    setError(null);
    setStatus(null);
    try {
      await api.del(`/api/exams/${e.id}`);
      setStatus(`Deleted "${e.title}".`);
      reloadWork();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the exam.");
    }
  }

  async function saveCourse(e: React.FormEvent) {
    e.preventDefault();
    if (!course) return;
    setCreating(true);
    setError(null);
    setStatus(null);
    try {
      await api.patch(`/api/courses/${course.id}`, {
        code: courseForm.code.trim().toUpperCase(),
        name: courseForm.name.trim(),
        year: courseForm.year,
        semester: courseForm.semester,
      });
      setSelected(course.id);
      setStatus("Course updated.");
      await loadCourses(course.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update the course.");
    } finally {
      setCreating(false);
    }
  }

  async function toggleArchive() {
    if (!course) return;
    const archiving = !course.archived;
    if (
      archiving &&
      !confirm(
        `Archive ${course.code} (${termLabel(course)})?\n\n` +
          `It disappears from course lists and stops accepting new submissions. ` +
          `All assignments, grades and shared questions are kept, and you can restore it later.`
      )
    )
      return;
    setError(null);
    try {
      await api.patch(`/api/courses/${course.id}`, { archived: archiving });
      setStatus(archiving ? `${course.code} archived.` : `${course.code} restored.`);
      await loadCourses(archiving ? undefined : course.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not change the course.");
    }
  }

  /**
   * Deleting an offering, for one created by mistake. Only offered while it is
   * empty -- the server refuses as soon as it holds assignments, exams or
   * anyone but its teacher, and archiving is the answer then.
   */
  async function deleteCourse() {
    if (!course) return;
    if (
      !confirm(
        `Delete ${course.code} — ${course.name} (${termLabel(course)})?\n\n` +
          `This cannot be undone. If the offering has been used at all, archive it instead.`
      )
    )
      return;
    setError(null);
    setStatus(null);
    setCreating(true);
    try {
      await api.del(`/api/courses/${course.id}`);
      // Out to the course list: the settings panel you were in belongs to a
      // course that no longer exists, and falling back to another course's
      // settings would look like the wrong one had been deleted.
      navigate("/courses");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete the course.");
    } finally {
      setCreating(false);
    }
  }

  const courseIsEmpty =
    !!course && course.assignmentCount === 0 && course.examCount === 0 && course.studentCount === 0;

  // The settings form is filled from whichever course the URL names, so
  // arriving on the Settings tab directly works the same as clicking into it.
  useEffect(() => {
    if (!editingCourse || !course) return;
    setCourseForm({
      code: course.code,
      name: course.name,
      year: course.year,
      semester: course.semester,
    });
    setShowNewCourse(false);
  }, [editingCourse, course?.id]);

  return (
    <div className="page">
      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      <CourseChrome
        section={editingCourse ? "settings" : "assignments"}
        course={course ?? null}
        courses={courses}
        staff={isStaff}
        onSelectCourse={(courseId) => setSelected(courseId, editingCourse)}
        actions={
          !isStaff ? (
            // "How am I doing in this course" had no page at all before: marks
            // lived one at a time on the cards below.
            course && (
              <Link to={`/courses/${course.id}/marks`}>
                <button>My marks</button>
              </Link>
            )
          ) : (
            <>
              <button onClick={() => setShowNewCourse((v) => !v)}>New course</button>
              {course && !course.archived && !editingCourse && (
                <>
                  <button onClick={createExam} disabled={creating}>New exam</button>
                  <button className="primary" onClick={createAssignment} disabled={creating}>
                    New assignment
                  </button>
                </>
              )}
            </>
          )
        }
      />

      {/*
        Students get this too, and it matters more to them than to staff.
        Archiving is how a course is retired at the end of a term, so over a
        degree every course a student has ever taken ends up archived -- and
        while this was staff-only, finishing a semester silently took away their
        own submissions, marks and code. The records were never deleted and the
        API served them the whole time; only the way in was missing.

        "Archived" is the teacher's word for what they did to the course. For a
        student it is simply last semester.
      */}
      {archivedCount > 0 && (
        <label className="row" style={{ marginBottom: 12, width: "auto" }}>
          <input
            type="checkbox"
            style={{ width: "auto" }}
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span style={{ marginBottom: 0 }} className="muted">
            {isStaff
              ? `Show archived courses (${archivedCount})`
              : `Show past courses (${archivedCount})`}
          </span>
        </label>
      )}

      {/* A past course still opens, and everything in it still reads -- but the
          compiler is closed server-side, so say why before a student tries. */}
      {!isStaff && course?.archived && (
        <div className="notice" style={{ marginBottom: 12 }}>
          This course has finished. Your work and marks are all here to read, but you cannot
          submit to it any more.
        </div>
      )}

      {editingCourse && course && (
        <div className="card">
          {/* No "back" button: the Assignments tab is right above, and two ways
              out of the same panel is the inconsistency this replaced. */}
          <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>COURSE DETAILS</div>
          <form onSubmit={saveCourse}>
            <div className="grid-2">
              <label>
                <span>Course ID / code</span>
                <input
                  value={courseForm.code}
                  onChange={(e) => setCourseForm({ ...courseForm, code: e.target.value })}
                  required
                />
              </label>
              <label>
                <span>Course name</span>
                <input
                  value={courseForm.name}
                  onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                  required
                />
              </label>
            </div>
            <div className="grid-2">
              <label>
                <span>Year</span>
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={courseForm.year}
                  onChange={(e) => setCourseForm({ ...courseForm, year: Number(e.target.value) })}
                  required
                />
              </label>
              <label>
                <span>Semester</span>
                <select
                  value={courseForm.semester}
                  onChange={(e) => setCourseForm({ ...courseForm, semester: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                    <option key={s} value={s}>Semester {s}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="row">
              <button className="primary" disabled={creating}>
                {creating ? "Saving…" : "Save changes"}
              </button>
              <button type="button" className={course.archived ? "" : "danger"} onClick={toggleArchive}>
                {course.archived ? "Restore course" : "Archive course"}
              </button>
              {/* Offered only where it can work: on an occupied course the
                  button's only outcome is the server's refusal. */}
              {courseIsEmpty && (
                <button type="button" className="danger" onClick={deleteCourse} disabled={creating}>
                  Delete course
                </button>
              )}
            </div>
            <p className="muted" style={{ fontSize: 13, marginBottom: 0 }}>
              {course.archived
                ? "This course is archived: it is hidden from lists and accepts no new submissions. Its records and library questions are intact."
                : "Archiving retires the offering without deleting anything — grades, submissions and shared questions all survive, and you can restore it."}
              {courseIsEmpty && " This one is still empty, so it can also be deleted outright."}
            </p>
          </form>
        </div>
      )}

      {showNewCourse && (
        <div className="card">
          <div className="between" style={{ marginBottom: 8 }}>
            <span style={{ color: "var(--muted)", fontSize: 12 }}>NEW COURSE</span>
            <button type="button" onClick={() => setShowNewCourse(false)}>Cancel</button>
          </div>
          <form onSubmit={createCourse}>
            <div className="grid-2">
              <label>
                <span>Course ID / code</span>
                <input
                  value={courseForm.code}
                  onChange={(e) => setCourseForm({ ...courseForm, code: e.target.value })}
                  placeholder="CS1101"
                  required
                  autoFocus
                />
              </label>
              <label>
                <span>Course name</span>
                <input
                  value={courseForm.name}
                  onChange={(e) => setCourseForm({ ...courseForm, name: e.target.value })}
                  placeholder="Problem Solving and Programming in C"
                  required
                />
              </label>
            </div>

            <div className="grid-2">
              <label>
                <span>Year</span>
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={courseForm.year}
                  onChange={(e) => setCourseForm({ ...courseForm, year: Number(e.target.value) })}
                  required
                />
              </label>
              <label>
                <span>Semester</span>
                <select
                  value={courseForm.semester}
                  onChange={(e) => setCourseForm({ ...courseForm, semester: Number(e.target.value) })}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((s) => (
                    <option key={s} value={s}>Semester {s}</option>
                  ))}
                </select>
              </label>
            </div>

            <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
              The same course code can run every semester — each is a separate offering, so last
              semester's assignments and results stay untouched. Reuse questions from a previous
              offering through the <Link to="/library">library</Link>.
            </p>

            <button className="primary" disabled={creating}>
              {creating ? "Creating…" : "Create course"}
            </button>
          </form>
        </div>
      )}

      {courses.length === 0 && !showNewCourse && (
        <div className="card muted">
          {isStaff
            ? "You have no courses yet. Use New course to create one — you will be enrolled as its teacher."
            : "You are not enrolled in any course yet. Ask your teacher to add you to the roster."}
        </div>
      )}

      {/* Settings is a section of its own: the work list belongs to the
          Assignments tab, and showing both would leave neither tab meaning
          anything. */}
      {!editingCourse && exams.length > 0 && (
        <>
          <div className="muted" style={{ fontSize: 12, margin: "18px 0 8px" }}>EXAMS</div>
          {exams.map((e) => {
            const now = Date.now();
            const open = now >= new Date(e.opensAt).getTime() && now <= new Date(e.closesAt).getTime();
            const done = e.myAttempt && e.myAttempt.status !== "IN_PROGRESS";
            return (
              <div className="card" key={e.id}>
                <div className="between">
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      {e.title} {!e.published && <span className="badge draft">draft</span>}{" "}
                      {open && <span className="badge warn">open now</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 13 }}>
                      {e.questionCount} question{e.questionCount === 1 ? "" : "s"}
                      {e.durationMinutes ? ` · ${e.durationMinutes} min` : ""}
                      {" · "}
                      {new Date(e.opensAt).toLocaleString()} — {new Date(e.closesAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="row">
                    {isStaff ? (
                      <>
                        {(e.flaggedAttempts ?? 0) > 0 && (
                          <span className="badge fail" title="Attempts that tripped the exam's proctoring rules">
                            {e.flaggedAttempts} flagged
                          </span>
                        )}
                        <span className="muted" style={{ fontSize: 13 }}>{e.attemptCount} attempt(s)</span>
                        <Link to={`/exams/${e.id}/edit`}><button>Manage</button></Link>
                        {/* An exam anyone has sat is a record: it can be
                            unpublished, never deleted. */}
                        {e.attemptCount === 0 && !course?.archived && (
                          <button className="danger" onClick={() => deleteExam(e)}>Delete</button>
                        )}
                      </>
                    ) : done ? (
                      <>
                        <span className="badge pass">submitted</span>
                        {/* Whether the marks are actually released is the
                            server's call; the page it opens says so if not. */}
                        <Link to={`/exams/${e.id}/result`}><button>Result</button></Link>
                      </>
                    ) : (
                      <Link to={`/exams/${e.id}`}>
                        <button className="primary" disabled={!open}>
                          {e.myAttempt ? "Resume" : open ? "Start" : "Not open"}
                        </button>
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <div className="muted" style={{ fontSize: 12, margin: "18px 0 8px" }}>ASSIGNMENTS</div>
        </>
      )}

      {!editingCourse && assignments.length === 0 && courses.length > 0 && (
        <div className="card muted">No assignments yet.</div>
      )}

      {!editingCourse && assignments.map((a) => (
        <div className="card" key={a.id}>
          <div className="between">
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>
                <Link to={`/assignments/${a.id}`}>{a.title}</Link>{" "}
                {!a.published && <span className="badge draft">draft</span>}
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                {a.maxMarks} marks ·{" "}
                {(a.partCount ?? 0) > 0
                  ? `${a.partCount} question${a.partCount === 1 ? "" : "s"}`
                  : `${a.testCaseCount} test case${a.testCaseCount === 1 ? "" : "s"}`}
                {a.dueAt && ` · due ${new Date(a.dueAt).toLocaleString()}`}
                {isStaff && (a.unfinishedParts ?? 0) > 0 && (
                  <span className="badge warn" style={{ marginLeft: 8 }}>
                    {a.unfinishedParts} unfinished
                  </span>
                )}
                {!isStaff && (a.partCount ?? 0) > 0 && (a.answeredParts ?? 0) > 0 && (
                  <> · {a.answeredParts} of {a.partCount} answered</>
                )}
              </div>
            </div>

            <div className="row">
              {!isStaff && a.latestSubmission && (
                <span className={`badge ${a.latestSubmission.score === null ? "warn" : a.latestSubmission.score >= a.maxMarks ? "pass" : "warn"}`}>
                  {a.latestSubmission.score ?? "—"} / {a.maxMarks}
                </span>
              )}
              {!isStaff && !a.latestSubmission && <span className="badge draft">not submitted</span>}
              {isStaff ? (
                <>
                  {/* A sheet gets one too: its page is the marking grid over
                      the questions inside it, since the sheet itself carries no
                      submissions. Without this the recommended shape -- one
                      sheet, several questions -- had no way in to marking. */}
                  <Link to={`/assignments/${a.id}/submissions`}>
                    <button>{(a.partCount ?? 0) > 0 ? "Marks" : "Submissions"}</button>
                  </Link>
                  <Link to={`/assignments/${a.id}/edit`}><button>Edit</button></Link>
                  {!course?.archived && (
                    <button className="danger" onClick={() => deleteAssignment(a)}>Delete</button>
                  )}
                </>
              ) : (
                <Link to={`/assignments/${a.id}`}>
                  <button className="primary">{a.latestSubmission ? "Open" : "Start"}</button>
                </Link>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
